// ════════════════════════════════════════════════════════════════════════════
// 📊 DayEndReport — Phase 1.2 Premium Z-Report & Executive Print Template UI
// ════════════════════════════════════════════════════════════════════════════
// Supports dual print modes: 📄 A4 Executive PDF/Paper & 🧾 80mm POS Thermal Receipt
// High-contrast, zero-toner-waste print CSS stylesheet with clean typography.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Printer, Calendar, RefreshCw, TrendingUp, Receipt,
  CreditCard, Banknote, Smartphone, Package, Clock, User, AlertCircle, FileText, CheckCircle2, ShieldCheck
} from 'lucide-react';

interface DayEndReportProps {
  storeApiKey: any;
  cashierToken?: any;
  onBack: () => void;
  customFieldLabel?: any;
}

interface ReportData {
  date: string;
  storeName: string;
  storeLogo?: string;
  storePhone?: string;
  storeAddress?: string;
  summary: {
    transactionCount: number;
    totalRevenue: number;
    totalDiscount: number;
    totalRefunds?: number;
    netRevenue: number;
    averageTicket: number;
  };
  byPaymentMethod: Record<string, number>;
  byCategory: Array<{ category: string; revenue: number }>;
  transactions: Array<{
    txRef: string;
    time: string;
    total: number;
    paymentMethod: string;
    cashierName: string;
    itemCount: number;
    customer?: any;
  }>;
}

const formatRs = (n: number) =>
  'Rs. ' + (Number(n) || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayStr = () => {
  const d = new Date();
  const lk = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Colombo' }));
  return `${lk.getFullYear()}-${String(lk.getMonth() + 1).padStart(2, '0')}-${String(lk.getDate()).padStart(2, '0')}`;
};

const DayEndReport: React.FC<DayEndReportProps> = ({ storeApiKey, cashierToken, onBack, customFieldLabel = 'Vehicle Number' }) => {
  const [date, setDate] = useState<string>(todayStr());
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printFormat, setPrintFormat] = useState<'a4' | 'thermal'>('a4');

  const fetchReport = useCallback(async () => {
    setLoading(true); setError(null);
    const token = cashierToken || localStorage.getItem('kangavena:cashierToken') || '';
    try {
      const res = await fetch(`/api/reports/day-end?date=${date}`, {
        headers: {
          'x-store-api-key': storeApiKey,
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error || 'Failed to load report'); return; }
      setData(json);
    } catch {
      setError('Cannot reach server');
    } finally {
      setLoading(false);
    }
  }, [storeApiKey, cashierToken, date]);

  useEffect(() => { void fetchReport(); }, [fetchReport]);

  const handlePrint = () => {
    window.print();
  };

  const methodIcon = (m: string) => {
    const lc = (m || '').toLowerCase();
    if (lc === 'cash') return <Banknote size={13} className="text-emerald-600 print:text-black" />;
    if (lc === 'card') return <CreditCard size={13} className="text-blue-600 print:text-black" />;
    return <Smartphone size={13} className="text-violet-600 print:text-black" />;
  };

  return (
    <div className="fixed inset-0 z-[200] bg-slate-900 text-slate-100 flex flex-col overflow-hidden print:static print:bg-white print:text-black print:overflow-visible">
      {/* ── Print-only Dynamic CSS Stylesheet ── */}
      <style>{`
        @media print {
          @page {
            size: ${printFormat === 'thermal' ? '80mm auto' : 'A4 portrait'};
            margin: ${printFormat === 'thermal' ? '3mm' : '10mm'};
          }
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * {
            visibility: hidden;
          }
          .dayend-printable, .dayend-printable * {
            visibility: visible;
          }
          .dayend-printable {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            color: #000000 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .dayend-no-print {
            display: none !important;
          }
          .print-card {
            background: #ffffff !important;
            color: #000000 !important;
            border: 1px solid #cbd5e1 !important;
            box-shadow: none !important;
          }
          .print-avoid-break {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>

      {/* ── Header Toolbar (Hidden in Print) ── */}
      <div className="dayend-no-print shrink-0 flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-slate-950/90 backdrop-blur-md border-b border-white/10 shadow-xl">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white transition-all border border-white/10"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-black tracking-wide text-white">Day-End / Z-Report</h1>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase bg-emerald-500/20 border border-emerald-500/30 text-emerald-400">
                Official Audit
              </span>
            </div>
            <p className="text-[11px] text-slate-400">{data?.storeName || 'Induni Enterprise'}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Format Mode Selector */}
          <div className="flex items-center p-1 rounded-xl bg-white/5 border border-white/10 text-[11px] font-bold">
            <button
              onClick={() => setPrintFormat('a4')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                printFormat === 'a4'
                  ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <FileText size={13} />
              <span>A4 Executive</span>
            </button>
            <button
              onClick={() => setPrintFormat('thermal')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                printFormat === 'thermal'
                  ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Receipt size={13} />
              <span>80mm Thermal</span>
            </button>
          </div>

          {/* Date Picker */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10">
            <Calendar size={13} className="text-slate-400" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={todayStr()}
              className="text-xs font-mono text-white bg-transparent outline-none cursor-pointer"
            />
          </div>

          {/* Refresh */}
          <button
            onClick={fetchReport}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white disabled:opacity-50 transition-colors border border-white/10"
            aria-label="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin text-emerald-400' : ''} />
          </button>

          {/* Print Button */}
          <button
            onClick={handlePrint}
            disabled={!data || loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:brightness-110 text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-emerald-500/20 disabled:opacity-40 active:scale-[0.97] transition-all"
          >
            <Printer size={14} />
            <span>Print Report</span>
          </button>
        </div>
      </div>

      {/* ── Main Scrollable Body ── */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-900 print:bg-white print:p-0 print:overflow-visible">
        {loading && !data && (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <RefreshCw size={36} className="animate-spin text-emerald-400 mb-3" />
            <p className="text-xs font-medium">Generating official Z-Report…</p>
          </div>
        )}

        {error && (
          <div className="max-w-md mx-auto mt-12 p-6 bg-rose-500/10 border border-rose-500/30 rounded-2xl flex items-start gap-3 text-rose-300">
            <AlertCircle size={20} className="text-rose-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-rose-200">Failed to load report</p>
              <p className="text-xs text-rose-400 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {data && (
          <div className={`dayend-printable mx-auto transition-all ${
            printFormat === 'thermal'
              ? 'max-w-[340px] bg-white text-black p-4 rounded-xl border border-slate-200 shadow-2xl font-mono'
              : 'max-w-4xl space-y-5'
          }`}>
            
            {/* ════════════════════════════════════════════════════════════════ */}
            {/* 🧾 THERMAL RECEIPT TEMPLATE (80mm) */}
            {/* ════════════════════════════════════════════════════════════════ */}
            {printFormat === 'thermal' ? (
              <div className="space-y-3 text-[11px] text-black">
                {/* Header */}
                <div className="text-center border-b border-dashed border-black pb-2 space-y-1">
                  {data.storeLogo && (
                    <div className="flex justify-center mb-1.5">
                      <img src={data.storeLogo} alt="Store Logo" className="h-10 w-auto object-contain max-w-[150px] filter grayscale print:filter-none" />
                    </div>
                  )}
                  <h1 className="text-sm font-black uppercase tracking-wider">{data.storeName}</h1>
                  {data.storeAddress && <p className="text-[9px] text-slate-700">{data.storeAddress}</p>}
                  {data.storePhone && <p className="text-[9px] text-slate-700">TEL: {data.storePhone}</p>}
                  <p className="text-[10px] font-bold mt-1">*** OFFICIAL Z-REPORT ***</p>
                  <p className="text-[9px]">DATE: {data.date}</p>
                  <p className="text-[9px]">TIME: {new Date().toLocaleTimeString('en-LK')}</p>
                </div>

                {/* Summary Table */}
                <div className="space-y-1 font-mono text-[10.5px]">
                  <div className="flex justify-between border-b border-dotted border-slate-400 py-0.5">
                    <span>TRANSACTIONS:</span>
                    <span className="font-bold">{data.summary.transactionCount}</span>
                  </div>
                  <div className="flex justify-between border-b border-dotted border-slate-400 py-0.5">
                    <span>GROSS REVENUE:</span>
                    <span className="font-bold">{formatRs(data.summary.grossRevenue || data.summary.totalRevenue)}</span>
                  </div>
                  <div className="flex justify-between border-b border-dotted border-slate-400 py-0.5">
                    <span>DISCOUNTS:</span>
                    <span>{formatRs(data.summary.totalDiscount)}</span>
                  </div>
                  <div className="flex justify-between border-b border-dotted border-slate-400 py-0.5">
                    <span>RETURNS:</span>
                    <span>{formatRs(data.summary.totalRefunds || 0)}</span>
                  </div>
                  <div className="flex justify-between border-b border-dashed border-black py-1 font-black text-[12px]">
                    <span>NET REVENUE:</span>
                    <span>{formatRs(data.summary.netRevenue)}</span>
                  </div>
                  <div className="flex justify-between py-0.5 text-[10px] text-slate-700">
                    <span>AVG TICKET:</span>
                    <span>{formatRs(data.summary.averageTicket)}</span>
                  </div>
                </div>

                {/* Payment Breakdown */}
                <div className="border-t border-dashed border-black pt-2 space-y-1">
                  <p className="font-bold text-[10px] uppercase tracking-wider">PAYMENT BREAKDOWN:</p>
                  {Object.entries(data.byPaymentMethod).map(([m, val]) => (
                    <div key={m} className="flex justify-between font-mono text-[10px]">
                      <span className="uppercase">{m}:</span>
                      <span>{formatRs(Number(val) || 0)}</span>
                    </div>
                  ))}
                </div>

                {/* Category Breakdown */}
                {data.byCategory.length > 0 && (
                  <div className="border-t border-dashed border-black pt-2 space-y-1">
                    <p className="font-bold text-[10px] uppercase tracking-wider">CATEGORIES:</p>
                    {data.byCategory.slice(0, 10).map(c => (
                      <div key={c.category} className="flex justify-between font-mono text-[10px]">
                        <span className="truncate max-w-[180px]">{c.category}:</span>
                        <span>{formatRs(c.revenue)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer */}
                <div className="border-t border-dashed border-black pt-2 text-center text-[9px] space-y-1">
                  <p className="font-bold">AUDIT COMPLETE</p>
                  <p>Printed by Kangavena POS OS</p>
                  <p className="text-[8px] text-slate-500">{new Date().toISOString()}</p>
                </div>
              </div>
            ) : (
              /* ════════════════════════════════════════════════════════════════ */
              /* 📄 A4 EXECUTIVE PRINT TEMPLATE */
              /* ════════════════════════════════════════════════════════════════ */
              <div className="space-y-5 text-slate-100 print:text-black">
                
                {/* ── Screen Card / Printable Header ── */}
                <div className="print-card rounded-3xl bg-slate-950/80 border border-white/10 p-6 sm:p-8 shadow-2xl relative overflow-hidden print:rounded-none print:border-b-2 print:border-black print:p-0 print:pb-4 print:shadow-none">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      {data.storeLogo ? (
                        <img src={data.storeLogo} alt="Store Logo" className="h-14 sm:h-16 w-auto object-contain max-w-[160px] rounded-xl bg-white p-1.5 border border-white/20 print:border-slate-400 print:shadow-none shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 print:text-black shrink-0">
                          <ShieldCheck size={26} />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white print:text-black">
                            {data.storeName}
                          </h2>
                          <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 print:bg-slate-100 print:text-black print:border-slate-400">
                            Verified Store
                          </span>
                        </div>
                        <p className="text-xs font-semibold text-emerald-400 print:text-slate-700 mt-0.5 uppercase tracking-widest">
                          Official Daily Z-Report & Audit Summary
                        </p>
                        {(data.storeAddress || data.storePhone) && (
                          <p className="text-[10px] text-slate-400 print:text-slate-600 font-mono mt-1">
                            {data.storeAddress && <span>{data.storeAddress}</span>}
                            {data.storeAddress && data.storePhone && <span> · </span>}
                            {data.storePhone && <span>Tel: {data.storePhone}</span>}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="text-left sm:text-right text-xs space-y-1 text-slate-400 print:text-slate-800 font-mono">
                      <div className="flex sm:justify-end items-center gap-2">
                        <span className="text-[10px] uppercase font-bold text-slate-500 print:text-slate-600">Report Date:</span>
                        <span className="font-bold text-white print:text-black bg-white/10 print:bg-slate-100 px-2 py-0.5 rounded-md border border-white/10 print:border-slate-300">
                          {data.date}
                        </span>
                      </div>
                      <p className="text-[10px]">Generated: {new Date().toLocaleString('en-LK')}</p>
                      <p className="text-[10px] font-semibold text-emerald-400 print:text-black">Status: Verified Closed</p>
                    </div>
                  </div>
                </div>

                {/* ── KPI Summary Cards ── */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 print:grid-cols-5 print:gap-2">
                  {[
                    { label: 'Total Sales', value: data.summary.transactionCount, icon: Receipt, sub: 'Bills Issued' },
                    { label: 'Gross Sales', value: formatRs(data.summary.grossRevenue || data.summary.totalRevenue), icon: TrendingUp, sub: 'Before Deductions' },
                    { label: 'Discounts', value: formatRs(data.summary.totalDiscount), icon: Package, sub: 'Promotions' },
                    { label: 'Returns', value: formatRs(data.summary.totalRefunds || 0), icon: ArrowLeft, sub: 'Refunded Items' },
                    { label: 'Avg Basket', value: formatRs(data.summary.averageTicket), icon: Clock, sub: 'Per Customer' },
                  ].map((k, i) => {
                    const Icon = k.icon;
                    return (
                      <div key={i} className="print-card rounded-2xl bg-white/[0.03] border border-white/10 p-4 shadow-lg print:rounded-xl print:p-2.5 print:border-slate-300">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400 print:text-slate-600">{k.label}</span>
                          <Icon size={14} className="text-emerald-400 print:text-black" />
                        </div>
                        <div className="text-base sm:text-lg font-black font-mono text-white print:text-black tracking-tight leading-none">
                          {k.value}
                        </div>
                        <p className="text-[9px] text-slate-500 print:text-slate-500 mt-1">{k.sub}</p>
                      </div>
                    );
                  })}
                </div>

                {/* ── Net Revenue Banner ── */}
                <div className="print-avoid-break rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 text-white shadow-xl border border-emerald-400/30 print:bg-slate-100 print:text-black print:border-2 print:border-black print:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-emerald-100 print:text-slate-700">
                        NET REVENUE (ACTUAL CASH IN HAND & ACCOUNTS)
                      </p>
                      <p className="text-2xl sm:text-4xl font-black font-mono tracking-tight mt-1">
                        {formatRs(data.summary.netRevenue)}
                      </p>
                    </div>
                    <div className="text-xs text-emerald-100 print:text-slate-600 font-mono bg-black/20 print:bg-white px-3.5 py-2 rounded-xl border border-white/20 print:border-slate-300 shrink-0">
                      <span>Formula: Gross Sales - (Discounts + Refunds)</span>
                    </div>
                  </div>
                </div>

                {/* ── Payment & Category Breakdown ── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 print:grid-cols-2 print:gap-4 print-avoid-break">
                  
                  {/* Payment Methods */}
                  <div className="print-card rounded-2xl bg-white/[0.03] border border-white/10 p-5 shadow-lg print:rounded-xl print:p-4 print:border-slate-300">
                    <h3 className="text-xs font-black uppercase tracking-wider text-white print:text-black mb-3 flex items-center gap-2">
                      <CreditCard size={15} className="text-emerald-400 print:text-black" />
                      Payment Method Breakdown
                    </h3>
                    {Object.keys(data.byPaymentMethod).length === 0 ? (
                      <p className="text-xs text-slate-500">No payment data recorded</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(data.byPaymentMethod)
                          .sort((a, b) => Number(b[1]) - Number(a[1]))
                          .map(([method, totalVal]) => {
                            const total = Number(totalVal) || 0;
                            const pct = data.summary.totalRevenue > 0 ? (total / data.summary.totalRevenue) * 100 : 0;
                            return (
                              <div key={method} className="space-y-1">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="flex items-center gap-1.5 font-bold uppercase text-slate-200 print:text-black">
                                    {methodIcon(method)} {method}
                                  </span>
                                  <span className="font-mono font-bold text-white print:text-black">
                                    {formatRs(total)} <span className="text-slate-400 print:text-slate-600 text-[10px]">({pct.toFixed(1)}%)</span>
                                  </span>
                                </div>
                                <div className="h-1.5 bg-white/10 print:bg-slate-200 rounded-full overflow-hidden">
                                  <div className="h-full bg-emerald-500 print:bg-black rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  {/* Category Breakdown */}
                  <div className="print-card rounded-2xl bg-white/[0.03] border border-white/10 p-5 shadow-lg print:rounded-xl print:p-4 print:border-slate-300">
                    <h3 className="text-xs font-black uppercase tracking-wider text-white print:text-black mb-3 flex items-center gap-2">
                      <Package size={15} className="text-emerald-400 print:text-black" />
                      Top Category Revenue
                    </h3>
                    {data.byCategory.length === 0 ? (
                      <p className="text-xs text-slate-500">No category sales recorded</p>
                    ) : (
                      <div className="space-y-2">
                        {data.byCategory.slice(0, 8).map(c => (
                          <div key={c.category} className="flex items-center justify-between text-xs py-1.5 border-b border-white/5 print:border-slate-200 last:border-0">
                            <span className="font-medium text-slate-300 print:text-black truncate max-w-[200px]">{c.category}</span>
                            <span className="font-mono font-bold text-white print:text-black">{formatRs(c.revenue)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>

                {/* ── Transaction Ledger Table ── */}
                <div className="print-card rounded-2xl bg-white/[0.03] border border-white/10 p-5 shadow-lg print:rounded-xl print:p-4 print:border-slate-300 print-avoid-break">
                  <h3 className="text-xs font-black uppercase tracking-wider text-white print:text-black mb-3 flex items-center gap-2">
                    <Receipt size={15} className="text-emerald-400 print:text-black" />
                    Transaction Audit Ledger ({data.transactions.length})
                  </h3>
                  {data.transactions.length === 0 ? (
                    <p className="text-xs text-slate-500">No transactions recorded for this date</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs font-mono">
                        <thead>
                          <tr className="border-b border-white/10 print:border-slate-400 text-slate-400 print:text-slate-700 uppercase text-[9px] font-bold">
                            <th className="pb-2">Time</th>
                            <th className="pb-2">Ref ID</th>
                            <th className="pb-2">Items</th>
                            <th className="pb-2">Cashier</th>
                            <th className="pb-2">Customer / Details</th>
                            <th className="pb-2 text-right">Method</th>
                            <th className="pb-2 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5 print:divide-slate-200">
                          {data.transactions.map((t) => (
                            <tr key={t.txRef} className="hover:bg-white/[0.02]">
                              <td className="py-2 text-slate-400 print:text-slate-600">
                                {new Date(t.time).toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="py-2 text-slate-300 print:text-slate-800 font-bold truncate max-w-[100px]">{t.txRef}</td>
                              <td className="py-2 text-slate-400 print:text-slate-700">{t.itemCount} items</td>
                              <td className="py-2 text-slate-300 print:text-slate-800">{t.cashierName}</td>
                              <td className="py-2 text-slate-400 print:text-slate-700">
                                {t.customer ? (
                                  <span>
                                    {t.customer.name} ({t.customer.phone})
                                    {t.customer.customFieldValue && ` | ${customFieldLabel}: ${t.customer.customFieldValue}`}
                                  </span>
                                ) : (
                                  <span className="text-slate-600 print:text-slate-400">Counter Sale</span>
                                )}
                              </td>
                              <td className="py-2 text-right font-bold uppercase text-emerald-400 print:text-black">{t.paymentMethod}</td>
                              <td className="py-2 text-right font-bold text-white print:text-black">{formatRs(t.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* ── Official Audit Sign-off Footer ── */}
                <div className="print-card rounded-2xl bg-white/[0.02] border border-white/10 p-5 print:rounded-none print:border-t-2 print:border-black print:p-0 print:pt-4 print:bg-white">
                  <div className="grid grid-cols-2 gap-8 text-center text-xs print:grid-cols-2">
                    <div className="border-t border-slate-700 print:border-black pt-2 space-y-1">
                      <p className="font-bold text-slate-300 print:text-black">Prepared By (Cashier / Manager)</p>
                      <p className="text-[10px] text-slate-500">Signature & Seal</p>
                    </div>
                    <div className="border-t border-slate-700 print:border-black pt-2 space-y-1">
                      <p className="font-bold text-slate-300 print:text-black">Verified & Approved By</p>
                      <p className="text-[10px] text-slate-500">Owner / Auditor</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-white/5 print:border-slate-200 text-center text-[9px] text-slate-500">
                    <p className="font-mono">Kangavena Commerce Engine · Verification Digest: {data.date}-{data.summary.transactionCount}-OK</p>
                  </div>
                </div>

              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default DayEndReport;
